// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SignInButton from "@werner/App/Components/Common/SignInButton";
import XngZone from "@werner/App/Components/Common/XngZone";
import PreviouslyPurchasedBadge from "@werner/App/Components/CustomBadges/PreviouslyPurchasedBadge";
import MoreInfoButton from "@werner/App/Components/MoreInfoButton/MoreInfoButton";
import Notify from "@werner/App/Components/Notify/Notify";
import ProductActions, { ProductActionsStyle } from "@werner/App/Components/Product/ProductActions";
import { xGoogleAnalyticsState } from "@werner/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@werner/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import setBreadcrumbActions from "@werner/App/Handlers/XNG/SetBreadcrumbActions";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import { getIsLeadCapture, getReplacementProducts } from "@werner/App/Store/Selectors/ProductSelectors";
import { getSettingsCollection } from "@werner/App/Store/Selectors/SettingsSelectors";
import ProductShareLink from "@werner/Overrides/Components/ProductShareLink";
import { constants } from "@werner/XDL/constants";
import Print from "@werner/XDL/Icons/Print";
import {
    BreadcrumbsActionsWrapper,
    color,
    createBreadcrumbActionLink,
    hideElementOnPrint,
    mergeWithCss,
    sharedTextBadgeStyle,
    StyledBox,
    xScreen,
} from "@werner/XDL/Utilities";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState, ownProps: HasProduct) => {
    return {
        isAuthenticated: xIsAuthenticated(state),
        brandName: getSettingsCollection(state).customSettings.brandName,
        isLeadCaputre: getIsLeadCapture(ownProps.product),
        hasVariants: !!ownProps.product.variantTraits?.length || ownProps.product.isVariantParent,
        xGoogleAnalytics: (state as unknown as xGoogleAnalyticsState).xGoogleAnalytics,
    };
};

const mapDispatchToProps = {
    setBreadcrumbActions,
};

export interface ProductDetailsViewStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;
    // XNG-Changes
    imagesSection?: InjectableCss;
    descriptionSection?: InjectableCss;
    attributesSection?: InjectableCss;
    priceSection?: InjectableCss;
    qtyValuesSection?: InjectableCss;
    actionsSection?: InjectableCss;
    qtyValueLabel?: TypographyProps;
    qtyValue?: TypographyProps;
    productActions?: ProductActionsStyle;
    signInWrapper?: InjectableCss;
    stickyWrapper?: InjectableCss;
    printLinkStyles?: LinkPresentationProps;
    replacementsContent?: InjectableCss;
    reportProductWrapper?: InjectableCss;
    textBadge?: InjectableCss;
}

// <XNG-Changes>
const sharedQtyValueTextStyles: TypographyProps = {
    as: "p",
    size: 15,
    weight: 500,
    css: css`
        letter-spacing: 0;
        line-height: 24px;
    `,
};
// </XNG-Changes>

export const viewStyles: ProductDetailsViewStyles = {
    reportProductWrapper: {
        css: css`
            & > div > div {
                max-width: 100%;
            }
        `,
    },
    container: {
        css: css`
            display: flex;
            width: 100%;
            margin-bottom: 80px;
            ${xScreen("lte", "sm")} {
                flex-wrap: wrap;
                row-gap: 24px;
            }
            @media print {
                margin-bottom: 150px;
            }
        `,
    },
    leftColumnGridItem: {
        css: css`
            display: flex;
            max-width: calc(100% - 300px);
            width: 100%;
            padding-right: 22px;
            flex-direction: column;
            & > .image-description-wrapper {
                display: flex;
                gap: 40px;
            }
            ${xScreen("lte", "sm")} {
                max-width: 308px;
                padding-right: 0;
            }
            ${xScreen("lte", constants.productDetails.breakpoints.md)} {
                max-width: unset;
                justify-content: center;
                align-items: center;
            }
            ${xScreen("lte", "xs")} {
                max-width: unset;
                padding-right: 0;
            }
            @media print {
                display: block;
            }
        `,
    },
    rightColumnGridItem: {
        css: css`
            width: 300px;
            @media print {
                ${({ isAuthenticated }: any) => (isAuthenticated ? "display: block;" : "display:none")}
            }
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    imagesSection: {
        css: css`
            width: 100%;
            max-width: fit-content;
            ${xScreen("lte", "sm")} {
                max-width: 308px;
            }
            ${xScreen("lte", "xs")} {
                max-width: unset;
            }
        `,
    },
    descriptionSection: {
        css: css`
            width: calc(100% - 412px);
            & .rockwell-message {
                margin-top: 36px;
                .bold {
                    font-weight: 500;
                }
                .color-support-01 {
                    color: ${color("danger")};
                }
            }
            ${xScreen("lte", "sm")} {
                margin-bottom: 36px;
                width: 100%;
            }
        `,
    },
    attributesSection: {
        css: css`
            @media print {
                page-break-inside: avoid;
            }
        `,
    },
    priceSection: {
        css: css`
            padding: 0 20px;
            & > div {
                padding-bottom: 24px;
                border-bottom: 1px solid ${color("common.border")};
                margin-bottom: 12px;
                &:empty {
                    display: none;
                }
            }
            &.has-leadcapture > div {
                border-bottom: none;
                padding-bottom: 0;
                text-align: center;
            }
        `,
    },
    qtyValuesSection: {
        css: css`
            padding: 0 20px;
            .variatns-wrapper {
                margin-bottom: 12px;
            }
            .quantities {
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 16px;
                &:after {
                    content: "|";
                    order: 1;
                    margin: 0 4px;
                }
            }
        `,
    },
    actionsSection: {
        css: css`
            padding: 20px;
            background: ${color("primary", 0.05)};
            margin-bottom: 12px;
            &:empty {
                display: none;
            }
            ${hideElementOnPrint}
        `,
    },
    qtyValueLabel: {
        ...mergeWithCss(sharedQtyValueTextStyles, {
            css: css`
                display: flex;
                margin-right: 4px;
                order: 0;
                &:last-child {
                    order: 2;
                }
            `,
        }),
    },
    qtyValue: {
        ...mergeWithCss(sharedQtyValueTextStyles, {
            weight: 400,
            css: css`
                &:before {
                    content: ":";
                    margin-right: 4px;
                }
            `,
        }),
    },
    productActions: {
        productQuantityOrderedStyles: {
            cssOverrides: {
                inputSelect: css`
                    width: 100px;
                    ${xScreen("lte", "xs")} {
                        width: 90px;
                    }
                `,
            },
        },

        addToCartButtonWrapper: {
            css: css`
                flex-grow: 1;
            `,
        },
    },
    signInWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            padding: 0 20px;
            & > button {
                width: 100%;
            }
        `,
    },
    stickyWrapper: {
        css: css`
            position: fixed;
            left: 0;
            background: ${color("common.white")};
            width: 100%;
            z-index: 1300;
            transition: all 300ms ease-out;
            opacity: 1;
            pointer-events: initial;
            min-height: 94px;
            height: auto;
            box-shadow: 0 0 15px 0 ${color("primary", 0.15)};
            border: 1px solid ${color("primary", 0.05)};
            &.stickyVisible {
                top: 0;
            }

            &.stickyHidden {
                top: -250px;
                &,
                & > * {
                    max-height: 0;
                    overflow: hidden;
                }
            }

            &.editingMode {
                position: unset;
            }

            ${xScreen("lte", "md")} {
                display: none;
            }

            ${hideElementOnPrint}
        `,
    },
    printLinkStyles: {
        ...createBreadcrumbActionLink(Print),
    },
    replacementsContent: {
        css: css`
            display: flex;
            flex-direction: column;
            .discontinued-label {
                font-weight: 500;
                margin-bottom: 15px;
            }
            .discontinued-parts-label {
                font-weight: 400;
            }
            .partnumbers {
                display: flex;
                flex-direction: column;
                gap: 4px;
            }
            .addtional-note {
            }
        `,
    },
    textBadge: {
        ...mergeWithCss(sharedTextBadgeStyle, {
            css: css`
                margin-top: 8px;
                margin-bottom: 12px;
            `,
        }),
    },
};

const styles = viewStyles;

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ProductDetailsView: React.FC<Props> = ({
    id,
    product,
    productInfo,
    isAuthenticated,
    brandName: settingsBrandName,
    isLeadCaputre,
    setBreadcrumbActions,
    hasVariants,
    xGoogleAnalytics,
}) => {
    const [stickyVisible, setStickyVisible] = useState(false);
    const { isEditing, isInShell } = useShellContext();

    const stickyHeader = useAppSelector(state => (state as CustomAppState).xHeader.stickyHeader || "90px");

    const isShellEditingMood = isInShell && isEditing;
    const isRockwellAutomation =
        product.brand?.name?.trim()?.toLowerCase() === settingsBrandName?.trim()?.toLowerCase();
    const productReplacements = getReplacementProducts(product).map(replacement => (
        <Link
            key={replacement.productName || replacement.productNumber}
            href={replacement.productUrl}
            aria-label={`${replacement.productName || replacement.productNumber}`}
        >
            {replacement.productName || replacement.productNumber}
        </Link>
    ));
    const hasReplacements = productReplacements.length > 0;

    let descElement: any = null;
    const handleSticky = () => {
        const isSticky = (isSticky: boolean) => {
            setStickyVisible(isSticky);
        };

        if (!descElement) {
            descElement = document.querySelector('[data-test-selector="productDetails_bottomSection"]');
            isSticky(false);
            return;
        }

        if (window.scrollY > parseFloat(stickyHeader) + 10) {
            isSticky(true);
        } else {
            isSticky(false);
        }
    };

    const handlePrintClick = () => {
        // Xng-Change WES-1779
        const dataLayer = getDataLayer();

        if (!dataLayer || !xGoogleAnalytics.pdpPrintEvent) {
            return;
        }

        const googleAnalyticsData = {
            event: xGoogleAnalytics.pdpPrintEventName,
            click_text: translate("Print"),
            affiliation: xGoogleAnalytics.affiliation,
            authentication_state: isAuthenticated ? "authenticated" : "unauthenticated",
            item_id: product.productNumber,
            destination: window.location.href,
        };
        dataLayer.push(googleAnalyticsData);

        openPrintDialog();
    };

    useEffect(() => {
        document.addEventListener("scroll", handleSticky, { passive: true });
        handleSticky();
        setBreadcrumbActions({
            breadcrumbActions: (
                <BreadcrumbsActionsWrapper data-test-selector="breadcrumb-actions">
                    <ProductShareLink
                        productProp={product}
                        productInfoProp={productInfo}
                        // extendedStyles={styles.shareLink}
                    />
                    <Link onClick={handlePrintClick} {...styles.printLinkStyles} aria-label="Print">
                        {translate("Print")}
                    </Link>
                </BreadcrumbsActionsWrapper>
            ),
        });
        return () => {
            document.removeEventListener("scroll", handleSticky);
            setBreadcrumbActions({
                breadcrumbActions: null,
            });
        };
    }, []);

    const replacementsNotify = hasReplacements ? (
        <StyledWrapper className="rockwell-message">
            <Notify showClose={false} type="warning">
                <Typography {...styles.replacementsContent}>
                    <Typography className="discontinued-label">
                        {siteMessage("Werner_PDP_Discontinued_Msg_Title")}
                    </Typography>
                    <Typography className="discontinued-parts-label">
                        {siteMessage("Werner_PDP_Discontinued_Msg_Suggested_Parts")}
                    </Typography>
                    <Typography className="partnumbers">{productReplacements}</Typography>

                    <Typography className="addtional-note">
                        {siteMessage("Werner_PDP_Discontinued_Msg_Note")}
                    </Typography>
                </Typography>
            </Notify>
        </StyledWrapper>
    ) : null;

    return (
        <>
            {!isShellEditingMood ? (
                <Hidden above="md">
                    <XngZone contentId={id} zoneName="ProductTitleMobile" hint="Mobile Version (put the title only)" />
                </Hidden>
            ) : (
                <XngZone
                    contentId={id}
                    zoneName="ProductTitleMobile"
                    hint="Product Title Mobile Version (put the title only)"
                />
            )}
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.leftColumnGridItem} data-test-selector="productDetails_leftColumn">
                    <StyledWrapper className="image-description-wrapper">
                        <StyledWrapper {...styles.imagesSection}>
                            <XngZone contentId={id} zoneName="Content0" hint="Zone For Image" />
                        </StyledWrapper>
                        <Hidden below="lg" {...styles.descriptionSection}>
                            <XngZone contentId={id} zoneName="Content1" hint="Zone For Description & Part Numbers" />
                            <PreviouslyPurchasedBadge product={product} textBadgeWrapperCss={styles.textBadge} />
                            {replacementsNotify}
                        </Hidden>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper
                    {...styles.rightColumnGridItem}
                    data-test-selector="productDetails_rightColumn"
                    {...{ isAuthenticated }}
                >
                    <StyledBox
                        css={css`
                            && {
                                ${isAuthenticated ? "padding: 28px 0 20px;" : ""}
                            }
                            &:empty {
                                display: none;
                            }
                        `}
                    >
                        {(isAuthenticated || isShellEditingMood) && (
                            <>
                                <StyledWrapper
                                    {...styles.priceSection}
                                    className={isLeadCaputre ? "has-leadcapture" : ""}
                                >
                                    <XngZone contentId={id} hint="Zone For Product Details Price" zoneName="Price" />
                                </StyledWrapper>

                                {(!isLeadCaputre || isShellEditingMood) && (
                                    <StyledWrapper {...styles.qtyValuesSection}>
                                        <StyledWrapper className="variatns-wrapper">
                                            <XngZone
                                                contentId={id}
                                                hint="Zone For Variant Selection"
                                                zoneName="Content3"
                                            />
                                        </StyledWrapper>
                                        <StyledWrapper className="quantities">
                                            <Typography {...styles.qtyValueLabel}>
                                                {translate("Min Qty")}
                                                <Typography {...styles.qtyValue}>{product.minimumOrderQty}</Typography>
                                            </Typography>
                                            <Typography {...styles.qtyValueLabel}>
                                                {translate("Qty Interval")}
                                                <Typography {...styles.qtyValue}>
                                                    {product.detail?.multipleSaleQty}
                                                </Typography>
                                            </Typography>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                                <StyledWrapper {...styles.actionsSection}>
                                    {isAuthenticated && isLeadCaputre ? (
                                        <MoreInfoButton />
                                    ) : (
                                        <ProductActions extendedStyles={styles.productActions} />
                                    )}
                                </StyledWrapper>
                                <XngZone contentId={id} zoneName="AddToList" hint="Zone For Add To List" />
                            </>
                        )}

                        {!isAuthenticated && hasVariants && (!isLeadCaputre || isShellEditingMood) && (
                            <StyledWrapper {...styles.qtyValuesSection}>
                                <StyledWrapper className="variatns-wrapper">
                                    <XngZone contentId={id} hint="Zone For Variant Selection" zoneName="Content3" />
                                </StyledWrapper>
                            </StyledWrapper>
                        )}

                        {!isAuthenticated && (
                            <StyledWrapper {...styles.signInWrapper}>
                                <SignInButton />
                            </StyledWrapper>
                        )}
                    </StyledBox>
                    {/* This zone should be shown for both anonymus & authenticated users */}
                    <StyledWrapper {...styles.reportProductWrapper}>
                        <XngZone contentId={id} zoneName="ReportProductData" hint="Zone For Report product data" />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            {!isShellEditingMood ? (
                <Hidden above="md" {...styles.descriptionSection}>
                    <XngZone
                        contentId={id}
                        zoneName="ProductPartNumbersMobile"
                        hint="Mobile Version (part numbers & availability)"
                    />
                    <PreviouslyPurchasedBadge product={product} textBadgeWrapperCss={styles.textBadge} />
                    {replacementsNotify}
                </Hidden>
            ) : (
                <>
                    <XngZone
                        contentId={id}
                        zoneName="ProductPartNumbersMobile"
                        hint="Mobile Version (part numbers & availability)"
                    />
                    <PreviouslyPurchasedBadge product={product} textBadgeWrapperCss={styles.textBadge} />
                </>
            )}
            <StyledWrapper {...styles.attributesSection} data-test-selector="productDetails_bottomSection">
                <XngZone contentId={id} zoneName="Content2" hint="Zone For Attributes specification" />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    // XNG-Changes: with Product & connecy
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsView)),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
